<template>
  <div class="freight-faixas">
    <div class="freight-faixas__header">
      <span class="freight-faixas__title">{{ title }}</span>

      <div class="freight-faixas__header-actions">
        <a-button type="link" class="freight-faixas__add" @click="addRow">
          <a-icon type="plus" />
          Nova faixa
        </a-button>

        <button
          type="button"
          class="freight-faixas__chevron"
          :aria-label="
            expanded ? 'Recolher faixas de peso' : 'Expandir faixas de peso'
          "
          @click="expanded = !expanded"
        >
          <a-icon :type="expanded ? 'up' : 'down'" />
        </button>
      </div>
    </div>

    <div v-if="expanded" class="freight-faixas__body">
      <div
        v-for="(faixa, index) in rows"
        :key="index"
        class="freight-faixas__faixa"
      >
        <div class="freight-faixas__row">
          <div class="freight-faixas__field">
            <label class="freight-faixas__label">
              <span class="freight-faixas__required">*</span> Faixa inicial
            </label>
            <a-input
              :value="formatDecimal(faixa.vlr_ini)"
              placeholder="Informe o valor da faixa inicial"
              @change="(e) => onDecimalChange(index, 'vlr_ini', e)"
            />
          </div>

          <div class="freight-faixas__field">
            <label class="freight-faixas__label">
              <span class="freight-faixas__required">*</span> Faixa final
            </label>
            <a-input
              :value="formatDecimal(faixa.vlr_fim)"
              placeholder="Informe o valor da faixa final"
              @change="(e) => onDecimalChange(index, 'vlr_fim', e)"
            />
          </div>

          <div class="freight-faixas__field">
            <label class="freight-faixas__label">
              <span class="freight-faixas__required">*</span> Valor
            </label>
            <a-input
              :value="formatCurrency(faixa.vlr)"
              placeholder="R$ 0,00"
              @change="(e) => onDecimalChange(index, 'vlr', e)"
            />
          </div>

          <div class="freight-faixas__field">
            <label class="freight-faixas__label">Fator conversão</label>
            <a-input
              :value="formatDecimal(faixa.fator_conv)"
              placeholder="Informe o fator de conversão"
              @change="(e) => onDecimalChange(index, 'fator_conv', e)"
            />
          </div>

          <span class="freight-faixas__acoes">
            <a-button
              v-if="isFaixaPreenchida(faixa)"
              type="link"
              class="freight-faixas__trash"
              :aria-label="`Remover faixa ${index + 1}`"
              @click="removeRow(index)"
            >
              <a-icon type="delete" />
            </a-button>
          </span>
        </div>

        <div class="freight-faixas__flags">
          <a-checkbox
            :checked="isOn(faixa.fixa)"
            @change="(e) => onFlagChange(index, 'fixa', e.target.checked)"
          >
            Fixa
          </a-checkbox>

          <a-checkbox
            :checked="isOn(faixa.excedente)"
            @change="(e) => onFlagChange(index, 'excedente', e.target.checked)"
          >
            Excedente
          </a-checkbox>
        </div>

        <div v-if="isOn(faixa.excedente)" class="freight-faixas__excedente">
          <span class="freight-faixas__excedente-label">Valor excedente</span>
          <a-input
            class="freight-faixas__excedente-input"
            :value="formatCurrency(faixa.vlr_exd)"
            placeholder="Informe o valor"
            @change="(e) => onDecimalChange(index, 'vlr_exd', e)"
          />
        </div>
      </div>

      <p v-if="veiculoError" class="freight-faixas__error">
        {{ veiculoError }}
      </p>

      <p v-if="overlapMessage" class="freight-faixas__error">
        {{ overlapMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  createEmptyFaixa,
  isFaixaPreenchida,
} from "../../services/freightTableConfigForm";

const ATIVO = 1;
const SIM = 1;
const NAO = 0;

export default {
  name: "FreightTableRouteFaixas",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    veiculoError: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "Faixas de cálculo",
    },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    rows() {
      return this.value && this.value.length ? this.value : [createEmptyFaixa()];
    },
    // §4.8 recusa faixas sobrepostas com erro em vlr_ini, e a mensagem do back
    // chega como "gt:<vlr_fim anterior>" — avisar aqui evita o 422 críptico.
    overlapMessage() {
      const preenchidas = this.rows.filter(
        (faixa) =>
          faixa.vlr_ini !== null &&
          faixa.vlr_ini !== "" &&
          faixa.vlr_fim !== null &&
          faixa.vlr_fim !== "",
      );

      for (let i = 1; i < preenchidas.length; i += 1) {
        if (
          Number(preenchidas[i].vlr_ini) <= Number(preenchidas[i - 1].vlr_fim)
        ) {
          return "As faixas não podem se sobrepor: o início de uma faixa precisa ser maior que o fim da anterior.";
        }
      }

      return "";
    },
  },
  methods: {
    isFaixaPreenchida,
    isOn(value) {
      return Number(value) === SIM;
    },
    formatDecimal(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return "";
      return num.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return "";
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    readDigits(e) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      return digits === "" ? null : Number(digits) / 100;
    },
    onDecimalChange(index, field, e) {
      const patch = {};
      patch[field] = this.readDigits(e);
      this.patchRow(index, patch);
    },
    onFlagChange(index, field, checked) {
      const patch = {};
      patch[field] = checked ? SIM : NAO;
      // Desligar o excedente sem limpar o valor deixaria um vlr_exd órfão indo
      // para a API por um campo que a tela já escondeu. `null` vira 0 no mapper.
      if (field === "excedente" && !checked) patch.vlr_exd = null;
      this.patchRow(index, patch);
    },
    patchRow(index, patch) {
      const next = this.rows.map((faixa, i) =>
        i === index ? { ...faixa, ...patch } : { ...faixa },
      );
      this.$emit("update:value", next);
    },
    addRow() {
      const anterior = this.rows[this.rows.length - 1] || {};
      const nova = createEmptyFaixa();
      // Encadeia a faixa nova a partir do fim da anterior — é a regra de não
      // sobreposição do §4.8 aplicada antes de o operador digitar.
      if (anterior.vlr_fim !== null && anterior.vlr_fim !== "") {
        nova.vlr_ini = Number(anterior.vlr_fim) + 0.01;
      }
      nova.status = ATIVO;
      this.expanded = true;
      this.$emit("update:value", this.rows.concat([nova]));
    },
    // A lixeira aparece em qualquer faixa preenchida, inclusive quando ela é a
    // única — mas o §4.8 exige ao menos uma faixa, então aí ela é esvaziada em
    // vez de removida.
    removeRow(index) {
      if (this.rows.length <= 1) {
        this.$emit("update:value", [createEmptyFaixa()]);
        return;
      }
      this.$emit(
        "update:value",
        this.rows.filter((_, i) => i !== index),
      );
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

// O bloco vive dentro do painel cinza de Transporte: o cabeçalho não tem caixa
// própria e herda esse cinza; só o conteúdo é branco, com raio apenas no topo e
// recuado 8px em relação ao card da vigência (medidas tiradas do frame 1308-32746).
.freight-faixas {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 18px 8px 14px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: @color-text-dark;
  }

  &__header-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__add {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 4px;
    color: @color-primary;
    font-size: 14px;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: @color-text-light;
    cursor: pointer;

    &:hover {
      color: @color-text-medium;
    }
  }

  &__body {
    margin: 0 8px;
    padding: 16px 10px;
    background: @color-bg-surface;
    border-radius: 10px 10px 0 0;
  }

  &__faixa + &__faixa {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
  }

  // As quatro colunas + a calha da lixeira; a faixa de valor excedente repete a
  // mesma grade para o input cair alinhado sob "Fator conversão".
  &__row,
  &__excedente {
    display: grid;
    grid-template-columns: repeat(4, 1fr) 40px;
    gap: 8px;
  }

  &__row {
    align-items: flex-end;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__label {
    font-size: 12px;
    line-height: 18px;
    color: @color-text-medium;
  }

  &__required {
    color: @color-danger;
    margin-right: 2px;
  }

  &__acoes {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 4px;
  }

  &__trash {
    color: @color-danger;
    padding: 0;
  }

  &__flags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
  }

  &__excedente {
    align-items: center;
    margin-top: 12px;
    padding: 10px 0;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 10px;
  }

  &__excedente-label {
    // Escapado: sem o ~"" o Less trata a barra como divisão e emite 0.25.
    grid-column: ~"1 / 4";
    padding-left: 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: @color-text-dark;
  }

  &__excedente-input {
    grid-column: 4;
  }

  &__error {
    margin: 12px 0 0;
    font-size: 12px;
    color: @color-danger;
  }
}
</style>
